import {
  Color,
  BackgroundPosition,
  BackgroundRepeat,
  BackgroundScrollEffect,
  BackgroundSettings,
  ImageSize,
  BackgroundType,
  BackgroundVideo,
  ColorType,
  StylesSettings,
  ImageSettings,
  Overlay,
} from "@/shared/types";

export function getSectionStyles(
  settings: StylesSettings | null | undefined,
  isIphone?: boolean
) {
  const styles: React.CSSProperties = {
    ...backgroundStyles(settings?.background, isIphone),
  };
  return styles;
}

export function backgroundStyles(
  background: BackgroundSettings | undefined | null,
  isIphone?: boolean
) {
  let styles: React.CSSProperties = {};

  const backgroundImages =
    background?.type === BackgroundType.Color
      ? backgroundColor(background?.color)
      : background?.type === BackgroundType.Image
      ? backgroundImage(background?.image?.src, background?.image?.overlay)
      : "";

  const backgroundPositions =
    background?.type === BackgroundType.Image
      ? background?.image?.position || BackgroundPosition.CenterCenter
      : BackgroundPosition.CenterCenter;

  const backgroundSizes =
    background?.type === BackgroundType.Image
      ? background?.image?.size || ImageSize.Cover
      : ImageSize.Cover;

  const backgroundRepeats =
    background?.type === BackgroundType.Image
      ? background?.image?.repeat || BackgroundRepeat.NoRepeat
      : BackgroundRepeat.NoRepeat;

  const backgroundScrollEffect = !isIphone
    ? background?.image?.scrollEffect
    : BackgroundScrollEffect.Scroll;

  const backgroundAttachments =
    background?.type === BackgroundType.Image
      ? backgroundScrollEffect
      : BackgroundScrollEffect.Scroll;

  styles = {
    backgroundImage: backgroundImages,
    backgroundPosition: backgroundPositions,
    backgroundSize: backgroundSizes,
    backgroundRepeat: backgroundRepeats,
    backgroundAttachment: backgroundAttachments,
  };

  return styles;
}

export function backgroundColor(backgroundColor: Color | undefined) {
  if (!backgroundColor) return "";
  if (backgroundColor?.type === ColorType.Theme) {
    const rgbaColor = convertHexToRgba(backgroundColor?.theme);
    return `linear-gradient(0deg,${rgbaColor},${rgbaColor})`;
  } else if (backgroundColor?.type === ColorType.Custom) {
    const rgbaColor = convertHexToRgba(backgroundColor?.custom);
    return `linear-gradient(0deg,${rgbaColor},${rgbaColor})`;
  } else if (backgroundColor?.type === ColorType.Gradient) {
    if (!backgroundColor?.gradient?.startColor) return "";
    return `linear-gradient(${backgroundColor?.gradient?.angle}deg,${backgroundColor?.gradient?.startColor} ${backgroundColor?.gradient?.startPosition}%,${backgroundColor?.gradient?.endColor} ${backgroundColor?.gradient?.endPosition}%)`;
  }
}

function backgroundImage(imageSrc?: string, overlay?: Overlay) {
  if (!imageSrc && !overlay) return "";
  if (overlay?.isEnabled) {
    return `${backgroundColor(overlay?.value)}, url(${imageSrc})`;
  } else {
    return `url(${imageSrc})`;
  }
}

export function backgroundImageOverlayStyles(overlay: Overlay | undefined) {
  if (!overlay) return "";
  let styles: React.CSSProperties = {};
  if (overlay?.isEnabled) {
    styles = {
      backgroundColor: backgroundColor(overlay?.value),
      position: "absolute",
      top: 0,
      left: 0,
      width: "100%",
      height: "100%",
      backgroundImage: backgroundColor(overlay?.value),
      backgroundPosition: BackgroundPosition.CenterCenter,
      backgroundSize: ImageSize.Cover,
      backgroundRepeat: BackgroundRepeat.NoRepeat,
      backgroundAttachment: BackgroundScrollEffect.Scroll,
    };
  }
  return styles;
}

export function backgroundVideoOverlayStyles(
  backgroundVideo: BackgroundVideo | undefined
) {
  if (!backgroundVideo) return "";
  let styles: React.CSSProperties = {};
  const backgroundOverlay = backgroundVideo?.overlay;
  if (backgroundOverlay?.isEnabled) {
    styles = {
      backgroundColor: backgroundColor(backgroundOverlay?.value),
      position: "absolute",
      top: 0,
      left: 0,
      width: "100%",
      height: "100%",
      backgroundImage: backgroundColor(backgroundOverlay?.value),
      backgroundPosition: BackgroundPosition.CenterCenter,
      backgroundSize: ImageSize.Cover,
      backgroundRepeat: BackgroundRepeat.NoRepeat,
      backgroundAttachment: BackgroundScrollEffect.Scroll,
    };
  }
  return styles;
}

export function buildImageStyles(imageSettings: ImageSettings | undefined) {
  if (!imageSettings) return {};
  let styles: React.CSSProperties = {};
  const imageSrc = imageSettings?.content?.src;
  const imageOverlay = imageSettings?.design?.overlay;
  const borderRadius = imageSettings?.design?.borderRadius;
  styles = {
    backgroundImage: backgroundImage(imageSrc, imageOverlay),
    backgroundPosition: BackgroundPosition.CenterCenter,
    backgroundSize: imageSettings?.design?.size || ImageSize.Cover,
    backgroundRepeat: BackgroundRepeat.NoRepeat,
    backgroundAttachment: BackgroundScrollEffect.Scroll,
    borderStartStartRadius: `${borderRadius?.startStart}px`,
    borderStartEndRadius: `${borderRadius?.startEnd}px`,
    borderEndStartRadius: `${borderRadius?.endStart}px`,
    borderEndEndRadius: `${borderRadius?.endEnd}px`,
  };
  return styles;
}

export function gradientBackgroundStyles(
  backgroundColorSettings: Color | undefined
) {
  if (!backgroundColorSettings) return "";
  let styles: React.CSSProperties = {};
  styles = {
    backgroundImage: backgroundColor(backgroundColorSettings),
    backgroundPosition: BackgroundPosition.CenterCenter,
    backgroundSize: ImageSize.Cover,
    backgroundRepeat: BackgroundRepeat.NoRepeat,
    backgroundAttachment: BackgroundScrollEffect.Scroll,
  };
  return styles;
}

function convertHexToRgba(color?: string) {
  if (!color) return "";
  const removeHash = color?.replace("#", "");
  const hex = removeHash?.length === 3 ? removeHash + removeHash : removeHash;
  const red = parseInt(hex?.substring(0, 2), 16);
  const green = parseInt(hex?.substring(2, 4), 16);
  const blue = parseInt(hex?.substring(4, 6), 16);
  const opacity = getOpacityFromHex(color);
  return `rgba(${red}, ${green}, ${blue}, ${opacity})`;
}

function getOpacityFromHex(color: string) {
  const removeHash = color?.replace("#", "");
  const hex = removeHash?.length === 3 ? removeHash + removeHash : removeHash;
  const opacity = parseInt(hex?.substring(6, 8), 16) || 255;
  return Math.round((opacity / 255) * 100) / 100;
}
